import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * LR Direct — batched pickup/drop counts for the loads currently on the board.
 *
 * Reads `direct_load_stops` ONLY (never the marketplace `loads`/`load_stops`).
 * Display-only: one batched query per set of visible load ids, grouped client
 * side into { picks, drops }. Loads with no stop rows (legacy) simply fall back
 * to 1-1 at render time, matching the detail panel's fallback semantics.
 */

export interface StopCounts {
  picks: number;
  drops: number;
}

export function useDirectLoadStopCounts(loadIds: string[]): Record<string, StopCounts> {
  const [counts, setCounts] = useState<Record<string, StopCounts>>({});
  const key = loadIds.slice().sort().join(",");

  useEffect(() => {
    const ids = key ? key.split(",") : [];
    if (ids.length === 0) {
      setCounts({});
      return;
    }
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("direct_load_stops" as any)
        .select("direct_load_id, stop_type")
        .in("direct_load_id", ids);

      if (cancelled) return;
      if (error) {
        console.warn("[LRDirect] stop count fetch failed", error);
        return;
      }

      const map: Record<string, StopCounts> = {};
      for (const row of (data ?? []) as any[]) {
        const id = row.direct_load_id as string;
        if (!map[id]) map[id] = { picks: 0, drops: 0 };
        if (row.stop_type === "pickup") map[id].picks += 1;
        else map[id].drops += 1;
      }
      setCounts(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return counts;
}

/** "4-1" chip text with the legacy 1-1 fallback. */
export function formatStopCounts(c: StopCounts | undefined): string {
  if (!c || (c.picks === 0 && c.drops === 0)) return "1-1";
  return `${Math.max(c.picks, 1)}-${Math.max(c.drops, 1)}`;
}
